// src/pages/InitSettingPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, tokenUtils } from "../hooks/useAuth";
import ContactModal from "../modal/ContactModal";
import { showConfirm } from "../modal/confirmModal";

export default function InitSettingPage() {
  const nav = useNavigate();
  const { api, logout } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [proposalList, setProposalList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // 페이지 진입 시 IAM 권한 재체크 및 신청 리스트 가져오기
  useEffect(() => {
    checkIAMAndLoadList();
  }, []);

  const checkIAMAndLoadList = async () => {
    try {
      // IAM 권한 재체크
      const recheckRes = await api.post("/iam/recheck_20250821");
      if (recheckRes.data.status && recheckRes.data.changed) {
        // 토큰이 변경되었으면 새로운 토큰 저장
        tokenUtils.setAccess(recheckRes.data.authorization);
        tokenUtils.setRefresh(recheckRes.data.refresh_token);

        // 워크스페이스 로딩 화면 표시
        setWorkspaceLoading(true);
        setLoadingProgress(0);

        // 5초 동안 프로그레스 애니메이션
        const interval = setInterval(() => {
          setLoadingProgress((prev) => {
            if (prev >= 100) {
              clearInterval(interval);
              return 100;
            }
            return prev + 2; // 50ms마다 2% 증가 (5초 = 100%)
          });
        }, 50);

        // 5초 후 페이지 이동
        setTimeout(() => {
          nav("/app");
        }, 5000);

        return;
      }

      // 신청 리스트 가져오기
      await loadProposalList();
    } catch (err) {
      console.error("IAM 체크 실패:", err);
    }
  };

  const loadProposalList = async () => {
    try {
      const res = await api.get("/proposal/list_20250821");
      if (res.data.status) {
        setProposalList(res.data.data || []);
      }
    } catch (err) {
      console.error("신청 리스트 로드 실패:", err);
    }
  };

  const handleJoinProject = async () => {
    if (!inviteCode.trim()) {
      setError("초대 코드를 입력해주세요.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await api.post("/proposal/apply_20250821", {
        invite_code: inviteCode,
      });

      if (res.data.status) {
        if (res.data.not_found_code) {
          setError("존재하지 않는 초대 코드입니다.");
        } else if (res.data.unmatched) {
          setError(
            "이메일이 일치하지 않습니다. 초대받은 이메일로 로그인해주세요."
          );
        } else {
          alert("신청이 완료되었습니다. 관리자 승인을 기다려주세요.");
          setIsJoinModalOpen(false);
          setInviteCode("");
          await loadProposalList();
        }
      }
    } catch (err) {
      setError("신청 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelProposal = async (approvalId) => {
    const confirmed = await showConfirm({
      title: "신청 취소",
      message: "정말 신청을 취소하시겠습니까?",
      confirmText: "취소하기",
      cancelText: "돌아가기",
      variant: "warning",
    });

    if (!confirmed) return;

    try {
      const res = await api.post("/proposal/apply_cancel_20250821", {
        approval_id: approvalId,
      });

      if (res.data.status) {
        alert("신청이 취소되었습니다.");
        await loadProposalList();
      }
    } catch (err) {
      alert("취소 중 오류가 발생했습니다.");
    }
  };

  const handleLogout = async () => {
    const confirmed = await showConfirm({
      title: "로그아웃",
      message: "정말 로그아웃 하시겠습니까?",
      confirmText: "로그아웃",
      cancelText: "취소",
      variant: "info",
    });

    if (confirmed) {
      await logout();
    }
  };

  // 승인 대기 중인 리스트 필터링
  const waitingProposals = proposalList.filter(
    (item) => item.approval_stage === "wait"
  );

  // SVG 아이콘들
  const HomeIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  );

  const LogOutIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
      <polyline points="16 17 21 12 16 7"></polyline>
      <line x1="21" y1="12" x2="9" y2="12"></line>
    </svg>
  );

  const SparklesIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"></path>
      <path d="M5 3v4"></path>
      <path d="M19 17v4"></path>
      <path d="M3 5h4"></path>
      <path d="M17 19h4"></path>
    </svg>
  );

  const Building2Icon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
      <path d="M10 6h4"></path>
      <path d="M10 10h4"></path>
      <path d="M10 14h4"></path>
      <path d="M10 18h4"></path>
    </svg>
  );

  const UserPlusIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <line x1="19" y1="8" x2="19" y2="14"></line>
      <line x1="22" y1="11" x2="16" y2="11"></line>
    </svg>
  );

  const ArrowRightIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12"></line>
      <polyline points="12 5 19 12 12 19"></polyline>
    </svg>
  );

  const PlusIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );

  const UsersIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  );

  const XIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  const CheckCircleIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="80"
      height="80"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
      <polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
  );

  // 워크스페이스 로딩 화면
  if (workspaceLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
        <div className="max-w-md w-full px-8">
          <div className="text-center">
            {/* 아이콘 애니메이션 */}
            <div className="mb-8 inline-flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full blur-xl opacity-50 animate-pulse"></div>
                <div className="relative bg-gradient-to-br from-blue-500 to-purple-600 rounded-full p-6">
                  {loadingProgress < 100 ? (
                    <SparklesIcon />
                  ) : (
                    <CheckCircleIcon />
                  )}
                </div>
              </div>
            </div>

            {/* 텍스트 */}
            <h2 className="text-3xl font-bold text-white mb-4">
              {loadingProgress < 100
                ? "새롭게 참여된 워크스페이스 발견"
                : "워크스페이스 준비 완료!"}
            </h2>
            <p className="text-gray-400 mb-8">
              {loadingProgress < 100
                ? "워크스페이스를 구성중입니다..."
                : "잠시 후 워크스페이스로 이동합니다."}
            </p>

            {/* 프로그레스 바 */}
            <div className="relative">
              <div className="bg-gray-800 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full transition-all duration-100 ease-linear"
                  style={{ width: `${loadingProgress}%` }}
                ></div>
              </div>
              <div className="mt-4 text-2xl font-bold">
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  {loadingProgress}%
                </span>
              </div>
            </div>

            {/* 로딩 도트 애니메이션 */}
            {loadingProgress < 100 && (
              <div className="mt-8 flex justify-center gap-2">
                <div
                  className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0ms" }}
                ></div>
                <div
                  className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                  style={{ animationDelay: "150ms" }}
                ></div>
                <div
                  className="w-2 h-2 bg-pink-500 rounded-full animate-bounce"
                  style={{ animationDelay: "300ms" }}
                ></div>
              </div>
            )}
          </div>
        </div>

        {/* 배경 효과 */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-600 to-purple-600 rounded-full filter blur-[200px] opacity-20 animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-[150px] opacity-30 animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600 rounded-full filter blur-[150px] opacity-30 animate-pulse delay-700"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-600 rounded-full filter blur-[200px] opacity-20 animate-pulse delay-1000"></div>
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 1px)`,
              backgroundSize: "50px 50px",
            }}
          ></div>
        </div>
      </div>

      <div className="relative z-10 min-h-screen flex">
        {/* 상단 네비게이션 */}
        <div className="absolute top-6 left-6 right-6 flex justify-between items-center">
          <button
            onClick={() => nav("/app")}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
          >
            <HomeIcon />
            <span className="font-medium">돌아가기</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
          >
            <LogOutIcon />
            <span className="font-medium">로그아웃</span>
          </button>
        </div>

        <div className="w-full max-w-6xl mx-auto flex flex-col items-center justify-center px-6 py-12">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl mb-6">
              <SparklesIcon />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                프로젝트 시작하기
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              아직 참여 중인 프로젝트가 없습니다.
              <br />
              새로운 프로젝트를 만들거나 기존 프로젝트에 참여해보세요.
            </p>
          </div>

          {/* 승인 대기 중인 신청 목록 */}
          {waitingProposals.length > 0 && (
            <div className="w-full max-w-4xl mb-8">
              <div className="bg-yellow-900/20 backdrop-blur-xl border border-yellow-800/50 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-yellow-400 mb-4">
                  승인 대기 중인 신청
                </h3>
                <div className="space-y-3">
                  {waitingProposals.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between bg-gray-900/50 rounded-xl p-4"
                    >
                      <div>
                        <p className="text-white font-medium">
                          {item.enterprise_name}
                        </p>
                        <p className="text-sm text-gray-400">승인 대기 중</p>
                      </div>
                      <button
                        onClick={() => handleCancelProposal(item.id)}
                        className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all"
                      >
                        취소
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Cards Section */}
          <div className="grid md:grid-cols-2 gap-8 w-full max-w-4xl">
            {/* Create Project Card */}
            <div
              className="group bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-3xl p-8 hover:border-blue-500/50 transition-all duration-300 cursor-pointer"
              onClick={() => setIsModalOpen(true)}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-blue-500/20 rounded-2xl flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                  <Building2Icon />
                </div>
                <div className="text-gray-600 group-hover:text-blue-400 group-hover:translate-x-1 transition-all">
                  <ArrowRightIcon />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                새 프로젝트 만들기
              </h2>
              <p className="text-gray-400 mb-6">
                팀을 위한 새로운 프로젝트를 생성하고 팀원들을 초대하여 협업을
                시작하세요.
              </p>

              <button className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-400 px-6 py-3 rounded-xl font-medium group-hover:bg-blue-600 group-hover:text-white transition-all">
                <PlusIcon />
                프로젝트 만들기
              </button>

              <div className="mt-6 pt-6 border-t border-gray-800">
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>프로젝트 관리자 권한</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <UsersIcon />
                    <span>팀원 초대 가능</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Join Project Card */}
            <div
              className="group bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-3xl p-8 hover:border-purple-500/50 transition-all duration-300 cursor-pointer"
              onClick={() => setIsJoinModalOpen(true)}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-purple-500/20 rounded-2xl flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                  <UserPlusIcon />
                </div>
                <div className="text-gray-600 group-hover:text-purple-400 group-hover:translate-x-1 transition-all">
                  <ArrowRightIcon />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-purple-400 transition-colors">
                프로젝트 참여하기
              </h2>
              <p className="text-gray-400 mb-6">
                초대 코드를 받으셨나요? 기존 프로젝트에 참여하여 팀과 함께
                작업을 시작하세요.
              </p>

              <button className="inline-flex items-center gap-2 bg-purple-600/20 text-purple-400 px-6 py-3 rounded-xl font-medium group-hover:bg-purple-600 group-hover:text-white transition-all">
                <UserPlusIcon />
                참여하기
              </button>

              <div className="mt-6 pt-6 border-t border-gray-800">
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <span>초대 코드 필요</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <UsersIcon />
                    <span>즉시 협업 시작</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ContactModal 컴포넌트 */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Join Project Modal */}
      {isJoinModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black"
            onClick={() => setIsJoinModalOpen(false)}
          ></div>
          <div className="relative bg-gray-900 border border-gray-800 rounded-2xl p-8 max-w-md w-full mx-4">
            <button
              onClick={() => setIsJoinModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <XIcon />
            </button>

            <h2 className="text-2xl font-bold text-white mb-6">
              프로젝트 참여하기
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  초대 코드
                </label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="초대 코드를 입력하세요"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                />
              </div>

              {error && (
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-3">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setIsJoinModalOpen(false)}
                  className="flex-1 px-4 py-3 bg-gray-800 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handleJoinProject}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "처리 중..." : "참여 신청"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes pulse {
          0%,
          100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.5;
          }
        }

        .animate-pulse {
          animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .delay-700 {
          animation-delay: 700ms;
        }

        .delay-1000 {
          animation-delay: 1000ms;
        }
      `}</style>
    </div>
  );
}
